// Basic
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import PropTypes from 'prop-types';
// Libs
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
// Store & actions
import actionsSims from '../../api/actionsSims';
// Components
import HeaderTitle from '@/common-utils/components/renders/modal/components/headers/HeaderTitle/HeaderTitle';
import Entity from '@/common-utils/components/renders/entity/Entity';
import Select from '@/common-utils/components/ui/select';
import Spinner from '@/common-utils/components/renders/spinner/Spinner';
import FooterStep from '@/common-utils/components/renders/modal/components/footers/FooterStep/FooterStep';
import FooterClose from '@/common-utils/components/renders/modal/components/footers/FooterClose/FooterClose';
import { Modal, ModalBody, ModalContent } from '@/common-utils/components/ui/modal';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/common-utils/components/ui/form';
// Common
import { useToast } from '@/common-utils/hooks/use-toast';
// Lang
import { nsSimCard } from '@/app/i18n/namespaces';
import useStepConfig from '@/common-utils/hooks/useStepConfig';

/**
 * @param root0
 * @param root0.isOpen
 * @param root0.setIsOpen
 * @param root0.setLaunchRefetch
 * @param root0.simCardId
 * @param root0.organizationName
 * @returns {JSX} ModalUpdateSimPlan
 */
function ModalUpdateSimPlan({ isOpen, setIsOpen, setLaunchRefetch, simCardId, organizationName }) {
    // Constants
    const STEPS_NUMBER = 2;
    const STEP_TITLE_PREFIX = 'modal.update_plan.steps';

    // Toast
    const { toast } = useToast();

    // State
    const [step, setStep] = useState(0);

    // Validation Schema
    const formSchema = z.object({
        plan: z.string().min(1)
    });

    const form = useForm({
        resolver: zodResolver(formSchema),
        mode: 'onChange',
        defaultValues: { plan: '' }
    });

    // Api calls
    // Retrieve list of offers
    const { data, isFetching, isError: erroFetchingPlans } = actionsSims.getSimOffersToUpdate(simCardId);

    // Update sim card offer
    const onSuccess = () => {
        setIsOpen(false);
        setLaunchRefetch(true);
        toast({
            description: <Entity entity='modal.update_plan.success' namespace={nsSimCard} />,
            variant: 'success'
        });
    };
    const onError = () => {
        setIsOpen(false);
        toast({
            description: <Entity entity='modal.update_plan.error' namespace={nsSimCard} />,
            variant: 'danger'
        });
    };
    const submitUpdateSimPlan = actionsSims.updateSimPlan(onSuccess, onError);

    // Hooks
    const { stepTitle, disabledNext } = useStepConfig(
        step,
        () => ({
            0: { stepTitle: `${STEP_TITLE_PREFIX}.plan_selection`, disabledNext: !form.formState.isValid },
            1: { stepTitle: `${STEP_TITLE_PREFIX}.confirmation`, disabledNext: false }
        }),
        [form.watch('plan'), form.formState.isValid]
    );

    // Functions
    const getSimPlans = () => {
        return data?.offers.map((offer) => { return { value: offer.shortDisplayName, name: offer.shortDisplayName }; });
    };

    // Constants
    const isPlanUpdatable = getSimPlans() && getSimPlans().length > 0;

    return (
        <Modal open={isOpen} onOpenChange={setIsOpen}>
            <ModalContent className='max-w-3xl'>
                <HeaderTitle
                    title='modal.update_plan.title'
                    namespace={nsSimCard}
                />
                <ModalBody>
                    <Entity
                        entity={stepTitle}
                        data={{ step: step + 1, maxStep: STEPS_NUMBER }}
                        namespace={nsSimCard}
                        className='mb-2 flex'
                    />
                    {step === 0 &&
                        <>
                            {isFetching &&
                                <Spinner className='panel-spinner' />

                            }
                            {!isFetching &&
                                <div className='text-center space-y-3'>
                                    {isPlanUpdatable &&
                                        <>
                                            <div>
                                                <Entity entity='modal.update_plan.plan_selection.text_1' namespace={nsSimCard} className='font-normal' />
                                            </div>
                                            <div>
                                                <Entity entity='modal.update_plan.plan_selection.text_2' namespace={nsSimCard} className='font-normal' />
                                            </div>
                                            <div>
                                                <Entity entity='modal.update_plan.plan_selection.text_3' namespace={nsSimCard} className='font-normal' />
                                            </div>
                                            <Form {...form}>
                                                <div className='flex justify-center items-center gap-2'>
                                                    <FormLabel htmlFor='plan' className='block text-sm font-medium text-gray-700'>
                                                        <Entity entity='modal.update_plan.plan_selection.select' namespace={nsSimCard} className='font-bold' />
                                                    </FormLabel>
                                                    <FormField
                                                        name='plan'
                                                        control={form.control}
                                                        render={({ field }) => (
                                                            <FormItem className='addresses-list-input'>
                                                                <FormControl>
                                                                    <Select
                                                                        {...field}
                                                                        options={getSimPlans()}
                                                                        namespace={nsSimCard}
                                                                        value={field.value}
                                                                    />
                                                                </FormControl>
                                                                <FormMessage />
                                                            </FormItem>
                                                        )}
                                                    />
                                                </div>
                                            </Form>
                                        </>
                                    }
                                    {!isPlanUpdatable && erroFetchingPlans &&
                                        (
                                            <div>
                                                <Entity entity='modal.update_plan.fetch_plans_error' namespace={nsSimCard} className='text-danger' />
                                            </div>
                                        )
                                    }
                                    {!isPlanUpdatable && !erroFetchingPlans &&
                                        (
                                            <div>
                                                <Entity entity='modal.update_plan.highest_plan_alert' namespace={nsSimCard} className='font-normal' />
                                            </div>
                                        )
                                    }
                                </div>
                            }
                        </>
                    }
                    {step === 1 &&
                        <div className='text-center  space-y-3'>
                            <div>
                                <Entity
                                    entity='modal.update_plan.confirmation.text_1'
                                    namespace={nsSimCard}
                                    data={{ simCardPlan: form.getValues('plan'), organizationName: organizationName ?? '' }}
                                    className='font-normal'
                                />
                            </div>
                            <div>
                                <Entity entity='modal.update_plan.confirmation.text_2' namespace={nsSimCard} className='font-normal' />
                            </div>
                            <div>
                                <Entity entity='modal.update_plan.confirmation.text_3' namespace={nsSimCard} className='font-normal' />
                            </div>
                        </div>
                    }
                </ModalBody>
                {isPlanUpdatable ?
                    <FooterStep
                        step={step}
                        setStep={setStep}
                        stepsNumber={STEPS_NUMBER}
                        onValidate={() => {
                            submitUpdateSimPlan.mutate({
                                simCardId: [simCardId],
                                offerId: data?.offers.find((simPlan) =>
                                    simPlan.shortDisplayName === form.getValues('plan'))?.uuid
                            });
                        }}
                        isLoading={submitUpdateSimPlan.isLoading}
                        disableValidation={disabledNext}
                    />
                    : <FooterClose />
                }
            </ModalContent>
        </Modal>
    );
}

ModalUpdateSimPlan.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    setIsOpen: PropTypes.func.isRequired,
    simCardId: PropTypes.string,
    organizationName: PropTypes.string,
    setLaunchRefetch: PropTypes.func
};
export default ModalUpdateSimPlan;